import React, { useState, useMemo, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CheckIcon, ClipboardCopyIcon, DotsVerticalIcon, ExternalLinkIcon, RefreshIcon, SparklesIcon, UserCircleIcon, XIcon } from "@heroicons/react/outline";
import CollapsibleSection from "./CollapsibleSection";
import { useModal, WorkflowOnlyContext } from "../contexts";
import ItemDetailHeader from "./ItemDetailHeader";
import ItemDetailSidebarHeader from "./ItemDetailSidebarHeader";
import UnsavedContentModal from "./UnsavedContentModal";
import { BLOCK_ICON_MAP, MIMETYPE_ICON_MAP, isItemActionable, OutputDetailRouteState, resolveMicrosoftMimeType } from "../utils";
import { ApprovalRequest, AbstractEmail, Workflow, WorkflowAutomationItem, Attachment, TeamUser, SignatureRequest, WorkflowOutputSuggestion } from "../interfaces";
import { deleteData, getData, OAuthError, patchData, postData, putData } from "../backend";
import OAuthModal from "./OAuthModal";
import OAuthModalMicrosoft from "./OAuthModalMicrosoft";
import ErrorModal from "./ErrorModal";
import Modal from "./Modal";
import ChangeRecipientModal from "./ChangeRecipientModal";
import ActivityLog from "./ActivityLog";
import { useTranslation } from "react-i18next";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import useUser from "../hooks/useUser";
import useSWR from "swr";
import moment from "moment";
import EmailEditor, { AttachmentAction, Ref as EmailEditorRef } from "./EmailEditor";
import SignatureRequestEmailContant from "./SignatureRequestEmailContant";
import SignatureRequestLinkPreview from "./SignatureRequestLinkPreview";
import CopyToClipboard from "react-copy-to-clipboard";
import PDFViewer from "./PDFViewer";
import { Popover } from "@headlessui/react";

export const APPROVAL_STATUS_MAP: Record<string, Array<string>> = {
  "PENDING": ["Waiting", "border-gray-600/5 bg-gray-100 text-gray-600"],
  "REQUESTED": ["Reviewing", "border-blue/5 bg-blue/10 text-blue"],
  "APPROVED": ["Approved", "border-green/5 bg-green/10 text-green"],
  "REJECTED": ["Changes Requested", "border-red/5 bg-red/10 text-red"],
  "CANCELED": ["Canceled", "border-red/5 bg-red/10 text-red"],
}

export const CHANGE_REQUEST_STATUS_MAP: Record<string, Array<string>> = {
  "PENDING": ["Waiting", "border-gray-600/5 bg-gray-100 text-gray-600"],
  "REQUESTED": ["Reviewing", "border-blue/5 bg-blue/10 text-blue"],
  "APPROVED": ["Changes", "border-green/5 bg-green/10 text-green"],
  "REJECTED": ["Rejcted", "border-red/5 bg-red/10 text-red"],
  "CANCELED": ["Canceled", "border-red/5 bg-red/10 text-red"],
}

const PAST_REVIEW_STAGES = ["REVIEW_APPROVED", "OUTPUT_PDF", "SIGNATURES_REQUEST", "SIGNATURES_PENDING", "SIGNATURES_COMPLETED", "EMAIL", "FINALISED"];

const STATUS_PILL_COLORS = {
  green: "text-green border-green/5 bg-green/10",
  blue: "text-blue border-blue/5 bg-blue/10",
  red: "text-red border-red/5 bg-red/10",
  gray: "text-gray-500 border-gray-500/5 bg-gray-100",
} as const;

function StatusPill({ text, color }: { text: string; color: keyof typeof STATUS_PILL_COLORS }) {
  return <span className={`text-xs font-semibold ${STATUS_PILL_COLORS[color]} border rounded-full px-2 py-0.5`}>{text}</span>;
}

interface Props {
  automationItemId: string;
  workflowId?: string;
}

function WorkflowOutputsDetail(props: Props) {
  const history = useHistory();
  const { t } = useTranslation();

  const { user, hasPermission } = useUser();

  const emailEditorRef = useRef<EmailEditorRef>(null);

  const [viewingItemId, setViewingItemId] = useState<string>(props.automationItemId);
  const [showPage, setShowPage] = useState(false);

  useEffect(() => {
    setViewingItemId(props.automationItemId);
  }, [props.automationItemId]);

  useEffect(() => { setShowPage(true); }, []);

  const { data: currentItem, mutate } = useSWR<WorkflowAutomationItem>(
    viewingItemId ? `/workflows/automation-items/${viewingItemId}` : null,
    getData,
    { refreshInterval: (data) => data?.status === "PENDING" ? 3000 : 0 },
  );

  const effectiveWorkflowId = props.workflowId ?? currentItem?.workflow?.id;

  const { data: workflow } = useSWR<Workflow>(
    effectiveWorkflowId ? `/workflows/${effectiveWorkflowId}/` : null,
    getData,
  );

  const location = useLocation<OutputDetailRouteState | undefined>();
  const routeSession = location.state;

  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);
  const [currentOutputGroup, setCurrentOutputGroup] = useState<"DOCUMENTS" | "EMAILS" | "SIGNATURE_REQUESTS">("DOCUMENTS");

  const [currentState, setCurrentState] = useState<"INITIAL" | "APPROVING" | "APPROVED" | "DELETING">("INITIAL");

  const [showRequestChanges, setShowRequestChanges] = useState(false);
  const [requestChangesMessage, setRequestChangesMessage] = useState<string>("The document(s) requires updates before approval can continue. Please review the suggested edits and resolve the comments.");

  const [copiedSignatureRequest, setCopiedSignatureRequest] = useState<string>();
  const [sidebarSendingEmailId, setSidebarSendingEmailId] = useState<string>();
  const [checkingDeliveryEmailId, setCheckingDeliveryEmailId] = useState<string>();
  const [currentDocVariant, setCurrentDocVariant] = useState<"original" | "pdf">("original");
  const [isSendingSequence, setIsSendingSequence] = useState(false);
  const [resendStatus, setResendStatus] = useState<"INITIAL" | "SENDING" | "RESENT" | "ERROR">("INITIAL");
  const [resendSignatureRequestId, setResendSignatureRequestId] = useState<string>();

  const [openSections, setOpenSections] = useState<Set<string>>(new Set(["DOCUMENTS"]));
  const [approvalCardOpen, setApprovalCardOpen] = useState(true);

  // Per-suggestion action state. ``pendingId`` is the id of whichever
  // suggestion is currently being applied/dismissed; ``errors`` is a per-id
  // error message that flips a row into its retry state.
  const [suggestionPendingId, setSuggestionPendingId] = useState<string | null>(null);
  const [suggestionErrors, setSuggestionErrors] = useState<Record<string, string>>({});

  // Email connection state
  const [emailConnected, setEmailConnected] = useState(true);
  const dominantEmailType = workflow?.signatureConfig?.emailType
    ?? workflow?.emails?.[0]?.emailType;

  function checkEmailScopes(onDisconnected?: (oauthUrl: string) => void) {
    if (!dominantEmailType) return;
    const check = dominantEmailType === "OUTLOOK"
      ? postData("/microsoft/access-token/", {})
      : postData("/auth/scopes/", { scopes: ["https://www.googleapis.com/auth/gmail.send"] });
    check
      .then(() => setEmailConnected(true))
      .catch(err => {
        if (err instanceof OAuthError) {
          setEmailConnected(false);
          onDisconnected?.(err.oauthUrl);
        }
      });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { checkEmailScopes(); }, [workflow?.id]);

  function handleReconnectEmail() {
    checkEmailScopes((oauthUrl) => {
      if (dominantEmailType === "OUTLOOK")
        openModal(<OAuthModalMicrosoft oauthUrl={oauthUrl} onClose={() => checkEmailScopes()} />);
      else
        openModal(<OAuthModal title={t("Portant requires your permission to send emails")} oauthUrl={oauthUrl} onClose={() => checkEmailScopes()} />);
    });
  }

  const reconnectEmailBar = (
    <div className="flex items-center justify-between px-3 py-2.5 border-t border-gray-200">
      <span className="font-gilroy font-semibold text-xs text-gray-400 max-w-[154px]">
        {t("Your credentials need to be refreshed to send emails.")}
      </span>
      <button className="h-8 px-3 text-xs font-semibold font-gilroy text-white bg-blue rounded-md hover:bg-blue/90 flex-shrink-0" onClick={handleReconnectEmail}>
        {t("Reconnect Email")}
      </button>
    </div>
  );

  // Green dot: track which output IDs the user has viewed
  const [viewedOutputIds, setViewedOutputIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("viewedOutputIds");
      return new Set<string>(stored ? JSON.parse(stored) : []);
    } catch { return new Set<string>(); }
  });

  function markOutputViewed(outputId: string) {
    setViewedOutputIds(prev => {
      if (prev.has(outputId)) return prev;
      const next = new Set(prev);
      next.add(outputId);
      try {
        const arr = Array.from(next);
        localStorage.setItem("viewedOutputIds", JSON.stringify(arr.slice(-1000)));
      } catch { }
      return next;
    });
  }

  // Signature recipient color mapping (1-indexed)
  const RECIPIENT_COLORS = ["text-blue", "text-purple", "text-green", "text-red"];
  const recipientNameMap: Record<number, string> = {
    1: workflow?.signatureConfig?.recipientName1 ?? "",
    2: workflow?.signatureConfig?.recipientName2 ?? "",
    3: workflow?.signatureConfig?.recipientName3 ?? "",
    4: workflow?.signatureConfig?.recipientName4 ?? "",
  };

  const latestApprovalRequest = currentItem?.approvalRequests.find((request) => request.status === "REQUESTED");

  const canApprove = currentItem?.stage === "REVIEW_PENDING"
    && isItemActionable(currentItem, user?.id ?? '', workflow?.review ?? undefined);

  // Outputs that have at least one Bluebird suggestion, plus a flat total
  // used to hide the section when there's nothing to surface.
  const outputsWithSuggestions = useMemo(
    () => user?.subscription?.featureSalesAgent
      ? currentItem?.outputs.filter(o => (o.suggestions?.length ?? 0) > 0) ?? []
      : [],
    [currentItem?.outputs, user?.subscription?.featureSalesAgent],
  );

  const totalSuggestions = outputsWithSuggestions.reduce(
    (sum, o) => sum + o.suggestions.length, 0,
  );

  const hasSignatureConfig = !!workflow?.signatureConfig;
  const hasSignatureRequests = (currentItem?.signatureRequests?.length ?? 0) > 0;
  const isAutoSendSig = workflow?.signatureConfig?.enableDraftRequests === false;

  const sendableSignatureRequets = currentItem?.signatureRequests.filter(isSendable);
  const linkableSignatureRequets = currentItem?.signatureRequests.filter(sr => !isSendable(sr));

  const approversList: Array<ApprovalRequest> = useMemo(() => {
    if (!currentItem?.approvalRequests)
      return [];

    let result: Array<ApprovalRequest> = [];
    for (const approvalRequest of currentItem.approvalRequests) {
      const index = result.findIndex(a => approvalRequest.approver.id === a.approver.id);
      if (index >= 0)
        result[index] = approvalRequest.status !== "PENDING" ? approvalRequest : result[index];
      else
        result.push(approvalRequest);
    }

    return result;
  }, [currentItem?.approvalRequests]);

  // Documents section: statusLabel + tag
  const docStatusLabel = (currentItem?.stage === "REVIEW_PENDING" && canApprove) ? t("Action Required") : undefined;
  const docTag = useMemo(() => {
    if (!workflow?.review || !currentItem) return undefined;
    if (PAST_REVIEW_STAGES.includes(currentItem.stage))
      return <StatusPill text={t("Approved")} color="green" />;
    if (currentItem.stage === "REVIEW_PENDING" && approversList.length <= 1)
      return <StatusPill text={t("Pending")} color="blue" />;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.stage, workflow?.review, approversList.length, t]);

  // Signature Request section: statusLabel + tag (3 phases)
  const { sigStatusLabel, sigTag } = useMemo(() => {
    const autoSendSigTag = isAutoSendSig
      ? <StatusPill text={t("Auto-Send: On")} color="green" />
      : <StatusPill text={t("Auto-Send: Off")} color="gray" />;

    if (!sendableSignatureRequets?.length) {
      return { sigStatusLabel: undefined, sigTag: autoSendSigTag };
    }
    const allCompleted = sendableSignatureRequets.every(sr => sr.status === "COMPLETED");
    const allPending = sendableSignatureRequets.every(sr => sr.status === "PENDING");
    const anyInProgress = sendableSignatureRequets.some(sr => ["REQUESTED", "ACCESSED"].includes(sr.status));

    if (allCompleted) {
      return { sigStatusLabel: undefined, sigTag: <StatusPill text={t("Signed")} color="green" /> };
    }
    const progressStopped = sendableSignatureRequets.find(sr => ["EXPIRED", "CANCELED", "ERROR"].includes(sr.status));
    if (progressStopped) {
      const label = progressStopped.status === "EXPIRED" ? t("Expired") : progressStopped.status === "CANCELED" ? t("Canceled") : t("Error");
      const color = progressStopped.status === "ERROR" ? "text-red" : "text-yellow";
      return { sigStatusLabel: <span className={color}>{label}</span>, sigTag: undefined };
    }
    if (anyInProgress || (!allPending && !allCompleted)) {
      return { sigStatusLabel: t("In Progress"), sigTag: undefined };
    }
    // Phase 1: Sequence not begun
    if (!isAutoSendSig && ["SIGNATURES_REQUEST", "SIGNATURES_PENDING"].includes(currentItem?.stage ?? "")) {
      return { sigStatusLabel: t("Action Required"), sigTag: undefined };
    }
    return { sigStatusLabel: undefined, sigTag: autoSendSigTag };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendableSignatureRequets, workflow?.signatureConfig?.enableDraftRequests, currentItem?.stage, t]);

  function handleOnCopiedSignatureRequest(id: string) {
    setCopiedSignatureRequest(id);
    setTimeout(() => setCopiedSignatureRequest(undefined), 2000);
  }

  function isSendable(sr: SignatureRequest) {
    return sr.recipientNumber > 0 && !["PREVIEW", "LINK", "LINK_UNPUBLISHED"].includes(sr.status);
  }

  function toggleSection(key: string) {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  // Compute default open section from stage + select matching content in main window
  useEffect(() => {
    setCurrentState("INITIAL");
    setShowRequestChanges(false);
    setSidebarSendingEmailId(undefined);
    setCurrentDocVariant("original");

    // Default to documents
    function resetToDocuments() {
      setCurrentOutputGroup("DOCUMENTS");
      setCurrentOutputIndex(0);
    }

    if (!currentItem || !workflow) {
      resetToDocuments();
      return;
    }

    const stage = currentItem.stage;
    const hasDocuments = currentItem.outputs.length > 0;
    const hasSignatures = hasSignatureRequests;
    const hasEmails = (workflow?.emails?.length ?? 0) > 0;

    let defaultSection = "DOCUMENTS";

    const sequenceNotBegun = (sendableSignatureRequets?.length ?? 0) > 0
      && sendableSignatureRequets!.every(sr => sr.status === "PENDING");
    const firstPendingEmailIdx = workflow?.emails?.findIndex((_e, i) =>
      currentItem.outputEmails?.find(oe => oe.email === workflow.emails[i].id)?.status === "PENDING"
    ) ?? -1;

    if (sequenceNotBegun && !isAutoSendSig && ["SIGNATURES_REQUEST", "SIGNATURES_PENDING"].includes(stage)) {
      defaultSection = "SIGNATURES";
    } else if (firstPendingEmailIdx >= 0 && ["EMAIL", "FINALISED", "SIGNATURES_COMPLETED"].includes(stage)) {
      defaultSection = `EMAIL_${firstPendingEmailIdx}`;
    }

    if (defaultSection === "DOCUMENTS" && !hasDocuments) {
      if (hasSignatures) defaultSection = "SIGNATURES";
      else if (hasEmails) defaultSection = "EMAIL_0";
    }

    // Select matching content in main window based on resolved section
    if (defaultSection.startsWith("EMAIL_")) {
      const emailIdx = parseInt(defaultSection.split("_")[1], 10);
      const emailConfig = workflow?.emails?.[emailIdx];
      const outputEmailIdx = emailConfig
        ? (currentItem.outputEmails?.findIndex(oe => oe.email === emailConfig.id) ?? -1)
        : -1;
      if (outputEmailIdx >= 0) {
        setCurrentOutputGroup("EMAILS");
        setCurrentOutputIndex(outputEmailIdx);
      } else {
        resetToDocuments();
      }
    } else if (defaultSection === "SIGNATURES") {
      const firstSendableIdx = currentItem.signatureRequests?.findIndex(sr => !["PREVIEW", "LINK", "LINK_UNPUBLISHED"].includes(sr.status)) ?? -1;
      if (firstSendableIdx >= 0) {
        setCurrentOutputGroup("SIGNATURE_REQUESTS");
        setCurrentOutputIndex(firstSendableIdx);
      } else {
        resetToDocuments();
      }
    } else {
      resetToDocuments();
      const firstOut = currentItem.outputs[0];
      if (firstOut?.filePdf || firstOut?.onedrivePdf || firstOut?.hubspotFilePdf) setCurrentDocVariant("pdf");
    }

    const sections = new Set([defaultSection]);
    if (defaultSection === "DOCUMENTS" && stage === "SIGNATURES_PENDING" && hasSignatures)
      sections.add("SIGNATURES");
    setOpenSections(sections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingItemId, currentItem?.stage, workflow?.id]);


  const isItemActionRequired = currentItem && user
    ? isItemActionable(currentItem, user.id, workflow?.review ?? undefined)
    : false;

  // Bootstrap actionable items for direct URL access (no route state)
  const [bootstrappedSession, setBootstrappedSession] = useState<string[] | null>(null);
  useEffect(() => {
    if (routeSession || bootstrappedSession) return;
    if (!effectiveWorkflowId || !user?.id) return;

    Promise.all([
      getData(`/workflows/${effectiveWorkflowId}/items/?page=1`) as Promise<{ results: WorkflowAutomationItem[]; numPages: number }>,
      getData(`/workflows/${effectiveWorkflowId}/`) as Promise<Workflow>,
    ])
      .then(([page, wf]) => {
        const actionable = page.results
          .filter(i => isItemActionable(i, user!.id, wf.review ?? undefined))
          .map(i => i.id);
        setBootstrappedSession(actionable);
      })
      .catch(() => setBootstrappedSession([]));
  }, [effectiveWorkflowId, user, routeSession, bootstrappedSession]);

  // Unified navigation from route state or bootstrap
  const sessionItemIds = routeSession?.sessionItemIds ?? bootstrappedSession ?? [];
  const currentSessionIndex = sessionItemIds.indexOf(viewingItemId);
  const hasSession = sessionItemIds.length > 0 && currentSessionIndex >= 0;
  const prevItemId = hasSession ? sessionItemIds[currentSessionIndex - 1] : undefined;
  const nextItemId = hasSession ? sessionItemIds[currentSessionIndex + 1] : undefined;

  const basePath = routeSession?.basePath
    ?? (effectiveWorkflowId ? `/w/${effectiveWorkflowId}/outputs` : '/outputs');

  const { openModal, closeModal } = useModal();

  function handleNavigateItem(itemId: string) {
    const navigate = () => {
      setViewingItemId(itemId);
      history.replace(`${basePath}/${itemId}`, location.state);
    };

    if (showRequestChanges) {
      openModal(<UnsavedContentModal onConfirm={() => { setShowRequestChanges(false); navigate(); }} />);
      return;
    }

    navigate();
  }

  function setCurrentItemOutput(outputGroup: "DOCUMENTS" | "SIGNATURE_REQUESTS" | "EMAILS", outputIndex: number) {

    if (outputGroup === "EMAILS") {
      const ok = emailEditorRef.current?.resetInitialEmail(currentItem?.outputEmails[outputIndex]!, () => setCurrentItemOutput("EMAILS", outputIndex));
      if (emailEditorRef.current && !ok)
        return;
    }

    if (outputGroup === "SIGNATURE_REQUESTS") {
      const ok = emailEditorRef.current?.resetInitialEmail(currentItem?.signatureRequests?.[outputIndex]!, () => setCurrentItemOutput("SIGNATURE_REQUESTS", outputIndex));
      if (emailEditorRef.current && !ok)
        return;
    }

    setCurrentOutputGroup(outputGroup);
    setCurrentOutputIndex(outputIndex);
  }

  async function handleApprove() {
    setCurrentState("APPROVING");
    await postData(`/workflows/automation-items/${viewingItemId}/approve/`)
      .then(mutate)
      .catch(console.error);

    setTimeout(moveToNextItem, 400);
  }

  async function handleApplySuggestion(suggestion: WorkflowOutputSuggestion) {
    setSuggestionPendingId(suggestion.id);
    setSuggestionErrors(prev => {
      const next = { ...prev };
      delete next[suggestion.id];
      return next;
    });
    try {
      await postData(`/workflows/output-suggestions/${suggestion.id}/apply/`);
      await mutate();
    } catch (err) {
      const message = err instanceof Error ? err.message : t("Failed to apply suggestion.");
      setSuggestionErrors(prev => ({ ...prev, [suggestion.id]: message }));
    } finally {
      setSuggestionPendingId(null);
    }
  }

  async function handleDismissSuggestion(suggestion: WorkflowOutputSuggestion) {
    setSuggestionPendingId(suggestion.id);
    setSuggestionErrors(prev => {
      const next = { ...prev };
      delete next[suggestion.id];
      return next;
    });
    try {
      await postData(`/workflows/output-suggestions/${suggestion.id}/dismiss/`);
      await mutate();
    } catch (err) {
      const message = err instanceof Error ? err.message : t("Failed to dismiss suggestion.");
      setSuggestionErrors(prev => ({ ...prev, [suggestion.id]: message }));
    } finally {
      setSuggestionPendingId(null);
    }
  }

  async function handleUndoSuggestion(suggestion: WorkflowOutputSuggestion) {
    setSuggestionPendingId(suggestion.id);
    setSuggestionErrors(prev => {
      const next = { ...prev };
      delete next[suggestion.id];
      return next;
    });
    try {
      const action = suggestion.status === "APPLIED" ? "unapply" : "restore";
      await postData(`/workflows/output-suggestions/${suggestion.id}/${action}/`);
      await mutate();
    } catch (err) {
      const message = err instanceof Error ? err.message : t("Failed to undo suggestion.");
      setSuggestionErrors(prev => ({ ...prev, [suggestion.id]: message }));
    } finally {
      setSuggestionPendingId(null);
    }
  }

  async function handleUpdateEmail(email: AbstractEmail): Promise<AbstractEmail> {
    const updatedEmail = await putData(`/workflows/output-emails/${currentOutputEmail!.id}/`, email);

    let outputEmails = [...currentItem?.outputEmails!]
    outputEmails[currentOutputIndex] = updatedEmail;

    mutate({ ...currentItem!, outputEmails })

    return email;
  }

  async function updateEmailAttachment(action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) {
    if (action === "CREATE")
      return postData(`/workflows/output-emails/${currentOutputEmail!.id}/attachments/`, attachment);

    else if (action === "DELETE")
      return deleteData(`/workflows/output-emails-attachments/${attachmentId}/`);

    else if (action === "UPDATE")
      return patchData(`/workflows/output-emails-attachments/${attachmentId}/`, { sharingMode: attachment!.sharingMode });
  }

  async function updateSender(teamUser: TeamUser) {
    return putData(`/workflows/output-emails/${currentOutputEmail!.id}/sender/`, { senderId: teamUser.id });
  }

  async function handleSendEmail() {
    const updatedEmail = await postData(`/workflows/output-emails/${currentOutputEmail!.id}/send/`);

    let outputEmails = [...currentItem?.outputEmails!]
    outputEmails[currentOutputIndex] = updatedEmail;

    mutate({ ...currentItem!, outputEmails });
  }

  async function handleCheckDelivery(outputEmailId: string, outputEmailIndex: number) {
    setCheckingDeliveryEmailId(outputEmailId);

    try {
      const updatedEmail = await postData(`/workflows/output-emails/${outputEmailId}/check-delivery/`);

      let outputEmails = [...currentItem?.outputEmails!]
      outputEmails[outputEmailIndex] = updatedEmail;

      mutate({ ...currentItem!, outputEmails });
    } finally {
      setCheckingDeliveryEmailId(undefined);
    }
  }

  async function updateSignatureRequestAttachment(action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) {
    if (action === "CREATE")
      return postData(`/signatures/signature-requests/${currentSignatureRequest!.id}/attachments/`, attachment);

    else if (action === "DELETE")
      return deleteData(`/signatures/signature-request-attachments/${attachmentId}/`);

    else if (action === "UPDATE")
      return patchData(`/signatures/signature-request-attachments/${attachmentId}/`, { sharingMode: attachment!.sharingMode });
  }

  async function handleUpdateSignatureRequest(email: AbstractEmail) {
    const signatureRequest: Partial<SignatureRequest> = { ...email, "recipientEmail": email.emailTo! }

    const updatedEmail = await putData(`/signatures/signature-requests/${currentSignatureRequest!.id}/`, signatureRequest);

    let signatureRequests = [...currentItem?.signatureRequests!]
    const index = signatureRequests.findIndex(sr => sr.id === currentSignatureRequest!.id);
    if (index >= 0) {
      signatureRequests[index] = updatedEmail;
    }

    mutate({ ...currentItem!, signatureRequests })

    return updatedEmail;
  }

  async function handleRejectItem() {
    const requestChangesUser = currentItem?.initiatingUser?.id;

    setCurrentState("DELETING");
    await postData(`/workflows/automation-items/${viewingItemId}/reject/`, { requestChangesMessage, requestChangesUser })
      .then(mutate)
      .catch(console.error);

    setTimeout(moveToNextItem, 400);
  }

  function moveToNextItem() {
    mutate();
    setCurrentState("INITIAL");
    setCurrentOutputIndex(0);

    if (nextItemId) {
      setViewingItemId(nextItemId);
      history.replace(`${basePath}/${nextItemId}`, location.state);
    } else {
      history.push(basePath);
    }
  }

  async function sendSignatureRequestSequence() {
    setIsSendingSequence(true);

    const nextSignatureRequest = sendableSignatureRequets?.filter(sr => sr.status === "PENDING")?.[0];
    const updatedSignatureRequest = await postData(`/signatures/signature-requests/${nextSignatureRequest!.id}/send/`);

    let signatureRequests = [...currentItem?.signatureRequests!]
    const idx = signatureRequests.findIndex(sr => sr.id === nextSignatureRequest!.id);
    if (idx >= 0) signatureRequests[idx] = updatedSignatureRequest;

    mutate({ ...currentItem!, signatureRequests })

    setIsSendingSequence(false);
  }

  async function handleRescindItem() {
    await postData(`/workflows/automation-items/${viewingItemId}/rescind/`)
      .then(mutate)
      .catch(console.error)
  }

  async function handleChangeRecipients() {
    await postData(`/workflows/automation-items/${viewingItemId}/change-recipients/`)
      .then(mutate)
      .catch(console.error)
  }

  function handleRerunItem() {
    postData(`/workflows/automation-items/${viewingItemId}/rerun/`)
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
        else
          openModal(<ErrorModal details={error} />);
      });
  }

  function handleDeleteItem() {
    openModal(
      <Modal
        title={<h3 className="font-gilroy font-bold text-red text-xl">{t("Delete Item")}</h3>}
        subtitle={t("Are you sure you want to delete this item? This action cannot be undone.")}
        secondaryButtonProps={{ text: t("Cancel"), onClick: closeModal }}
        primaryButtonProps={{
          className: "btn-red",
          text: t("Confirm"),
          onClick: () => {
            closeModal();
            mutate({ ...currentItem!, status: "CANCELLED" } as WorkflowAutomationItem, false);
            deleteData(`/workflows/automation-items/${viewingItemId}/`)
              .then(() => setTimeout(() => history.push(props.workflowId ? `/w/${props.workflowId}/outputs` : "/outputs"), 800))
              .catch(console.error);
          },
        }}
        size="sm"
      />
    );
  }

  function handleResendSignatureRequest(id: string) {
    setResendStatus("SENDING");
    setResendSignatureRequestId(id);

    postData(`/signatures/resend/${id}/`)
      .then(() => {
        setResendStatus("RESENT");
        setTimeout(() => {
          setResendStatus("INITIAL");
          setResendSignatureRequestId(undefined);
        }, 5000);
      })
      .catch(() => setResendStatus("ERROR"));
  }

  // Workflow mismatch guard: if workflowId prop doesn't match the item's actual workflow, redirect
  if (props.workflowId && currentItem && currentItem.workflow?.id !== props.workflowId) {
    history.replace(`/w/${props.workflowId}/outputs`);
    return null;
  }

  if (!currentItem || !user || !workflow)
    return (
      <div className="relative flex flex-col font-sans bg-gray-100" style={{ height: `calc(100vh)` }}>
        <div className="w-full h-full flex flex-col items-center justify-center">
          <div className='w-24 h-24'>
            <img src="https://static.portant.co/portant-loading-white.svg" alt="Loading Logo" />
          </div>
        </div>
      </div>
    );

  const currentOutput = currentOutputGroup === "DOCUMENTS" ? currentItem.outputs[currentOutputIndex] : undefined;
  const currentOutputEmail = currentOutputGroup === "EMAILS" ? currentItem.outputEmails[currentOutputIndex] : undefined;
  const currentSignatureRequest = currentOutputGroup === "SIGNATURE_REQUESTS" ? currentItem.signatureRequests?.[currentOutputIndex] : undefined;

  const docDisplayItems = currentItem.outputs.flatMap((output, origIdx) => {
    const items: Array<{ output: typeof output; variant: "original" | "pdf"; origIdx: number }> = [];
    if (output.filePdf || output.onedrivePdf || output.hubspotFilePdf) items.push({ output, variant: "pdf", origIdx });
    if (output.file || output.onedriveFile || output.hubspotFileMicrosoft) items.push({ output, variant: "original", origIdx });
    if (items.length === 0) items.push({ output, variant: "original", origIdx });
    return items;
  }).sort((a, b) => (a.variant === "pdf" ? 0 : 1) - (b.variant === "pdf" ? 0 : 1));

  const hubspotPortalId = workflow.team?.hubspotCredentials?.hubspotPortalId || user?.hubspotCredentials?.hubspotPortalId;

  return (
    <WorkflowOnlyContext.Provider value={{ workflow }}>
      <div className="relative flex flex-col font-sans bg-gray-100" style={{ height: `calc(100vh)` }}>
        <TransitionWorkflowPage
          show={showPage}
          header={
            <ItemDetailHeader
              currentItem={currentItem}
              workflow={workflow}
              prevItemId={prevItemId}
              nextItemId={nextItemId}
              sessionPosition={hasSession ? currentSessionIndex + 1 : undefined}
              sessionTotal={hasSession ? sessionItemIds.length : undefined}
              onNavigate={handleNavigateItem}
              workflowId={props.workflowId}
              isActionRequired={isItemActionRequired}
            />
          }
        >
          <TransitionWorkflowPageSideBar>
            <ItemDetailSidebarHeader
              currentItem={currentItem}
              workflow={workflow}
              hubspotPortalId={hubspotPortalId}
              itemId={viewingItemId}
              dateFormat={user?.dateFormat}
              onRerun={handleRerunItem}
              onDelete={handleDeleteItem}
              onRescind={handleRescindItem}
              onRefresh={mutate}
              canRerun={workflow.source?.sourceType !== "SIGNATURE_LINK" && hasPermission(workflow.team, "workflows.run")}
              canDelete={hasPermission(workflow.team, "workflows.edit")}
              canRescind={!!workflow.review && !!workflow.signatureConfig && currentItem.stage === "SIGNATURES_PENDING"}
            />
            {!showRequestChanges &&
              <div className="flex flex-col">
                {/* ===== DOCUMENTS SECTION ===== */}
                <CollapsibleSection
                  title={t("Documents")}
                  statusLabel={docStatusLabel}
                  tag={approversList.length > 1 && openSections.has("DOCUMENTS") ? undefined : docTag}
                  isOpen={openSections.has("DOCUMENTS")}
                  onToggle={() => toggleSection("DOCUMENTS")}
                  hidden={currentItem.outputs.length === 0}
                >
                  {/* Approval Status sub-card */}
                  {workflow?.review && approversList.length > 1 && (
                    <div className="border border-gray-200 rounded-xl mb-3">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2.5 text-left"
                        onClick={() => setApprovalCardOpen(prev => !prev)}
                      >
                        <span className="text-sm font-semibold text-black">{t("Approval Status")}</span>
                        {currentItem.stage === "REVIEW_PENDING" && <StatusPill text={t("Pending")} color="blue" />}
                        {PAST_REVIEW_STAGES.includes(currentItem.stage) && <StatusPill text={t("Approved")} color="green" />}
                        {currentItem.stage === "REVIEW_REJECTED" && <StatusPill text={t("Changes Requested")} color="red" />}
                      </button>
                      {approvalCardOpen && (
                        <div className="flex flex-col gap-2 px-3 pb-3 border-t border-gray-200 pt-2">
                          {approversList.map((ar) => {
                            const isWaiting = ar.status === "REQUESTED"
                              && workflow?.review?.approvalsInSequence
                              && latestApprovalRequest?.id !== ar.id;
                            const displayStatus = isWaiting ? "PENDING" : ar.status;
                            const statusMap = ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP;
                            return (
                              <div key={ar.id} className="flex items-center gap-2 text-sm">
                                <img className="h-5 w-5 rounded-full border-2 drop-shadow overflow-hidden flex-shrink-0" src={ar.approver.avatar} alt="User Avatar" />
                                <span className="font-semibold text-gray-600 truncate">{ar.approver.fullName}</span>
                                <span className={`ml-auto text-xs whitespace-nowrap px-2 py-0.5 font-semibold rounded-full border ${statusMap[displayStatus]?.[1]}`}>
                                  {statusMap[displayStatus]?.[0]}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Documents list card */}
                  <div className={`border rounded-xl ${currentOutputGroup === "DOCUMENTS" ? "border-blue" : "border-gray-200"}`}>
                    <div className="flex flex-col p-3 gap-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-black">{t("Output Documents")}</div>
                        <div className="flex items-center gap-1.5">
                          {workflow.outputFolder?.url && (
                            <a href={workflow.outputFolder.url} target="_blank" rel="noreferrer" className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100">
                              <img className="h-4 w-4" src={BLOCK_ICON_MAP["GOOGLE_DRIVE"]} alt="Google Drive" />
                            </a>
                          )}
                          {!workflow.onedriveDisabled && currentItem.outputs.some(o => o.onedriveFile) && (
                            <a href={currentItem.outputs.find(o => o.onedriveFile)?.onedriveFile?.fileUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100">
                              <img className="h-4 w-4" src={BLOCK_ICON_MAP["ONEDRIVE"]} alt="OneDrive" />
                            </a>
                          )}
                          {workflow.source?.sourceType === "HUBSPOT" && hubspotPortalId && currentItem.sourceItemId && (
                            <a href={`https://app.hubspot.com/contacts/${hubspotPortalId}/record/${workflow.source.hubspotConfig?.hubspotObjectTypeId}/${currentItem.sourceItemId}/`} target="_blank" rel="noreferrer" className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100">
                              <img className="h-4 w-4" src={BLOCK_ICON_MAP["HUBSPOT"]} alt="HubSpot" />
                            </a>
                          )}
                        </div>
                      </div>
                      {docDisplayItems.map((item, index) => {
                        const { output, variant, origIdx } = item;
                        const displayName = variant === "pdf"
                          ? (output.onedrivePdf?.fileName ?? output.filePdf?.name ?? output.hubspotFilePdf?.fileName ?? output.outputName)
                          : (output.onedriveFile?.fileName ?? output.file?.name ?? output.hubspotFileMicrosoft?.fileName ?? output.filePdf?.name ?? output.outputName);
                        const displayMime = variant === "pdf"
                          ? (output.onedrivePdf?.fileMimeType ?? output.filePdf?.mimeType ?? "application/pdf")
                          : (output.onedriveFile
                            ? resolveMicrosoftMimeType(output.onedriveFile.fileMimeType, output.onedriveFile.fileName)
                            : (output.file?.mimeType ?? (output.hubspotFileMicrosoft
                              ? resolveMicrosoftMimeType(undefined, output.hubspotFileMicrosoft.fileName, output.hubspotFileMicrosoft.fileExt)
                              : (output.fileMicrosoft
                                ? resolveMicrosoftMimeType(output.fileMicrosoft.mimeType, output.fileMicrosoft.name)
                                : (output.filePdf?.mimeType ?? "application/pdf")))));
                        const isSelected = currentOutputGroup === "DOCUMENTS" && currentOutputIndex === origIdx && currentDocVariant === variant;
                        return (
                          <div
                            key={`${output.id}-${variant}`}
                            className={`flex items-center gap-2 h-10 px-2 rounded-md cursor-pointer border border-100 ${isSelected ? "selected-item-border" : "hover:bg-gray-50"}`}
                            onClick={() => { markOutputViewed(output.id); setCurrentOutputIndex(origIdx); setCurrentOutputGroup("DOCUMENTS"); setCurrentDocVariant(variant); }}
                          >
                            <img className="h-4 w-4 flex-shrink-0" src={MIMETYPE_ICON_MAP[displayMime]} alt="File Icon" />
                            <span className={`text-sm truncate font-semibold ${isSelected ? "text-black" : "text-gray-600"}`}>
                              {displayName}
                            </span>
                            {index > 0 && !viewedOutputIds.has(output.id) && <div className="bg-green w-2 h-2 rounded-full flex-shrink-0 ml-auto" />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Action buttons */}
                    {canApprove && (
                      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-200">
                        {(currentState === "INITIAL" && !latestApprovalRequest?.isChangeRequest && latestApprovalRequest?.approver.id !== currentItem.initiatingUser?.id && currentItem.approvalRequests.length > 0) && (
                          <button className="flex-1 text-sm font-semibold py-2 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50" onClick={() => setShowRequestChanges(true)}>
                            {t("Request Changes")}
                          </button>
                        )}
                        {currentState === "INITIAL" && (
                          <button className="flex-1 text-sm font-semibold py-2 rounded-md bg-blue text-white hover:bg-blue/90" onClick={handleApprove}>
                            {latestApprovalRequest?.isChangeRequest ? t("Submit changes") : currentItem.outputs.length > 1 ? t("Approve All") : t("Approve")}
                          </button>
                        )}
                        {currentState === "APPROVING" && (
                          <button className="flex-1 text-sm font-semibold py-2 rounded-md bg-blue text-white" disabled>
                            {t("Processing...")}
                          </button>
                        )}
                        {currentState === "APPROVED" && (
                          <button className="flex-1 text-sm font-semibold py-2 rounded-md bg-green text-white" disabled>
                            {t("Approved")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </CollapsibleSection>

                <CollapsibleSection
                  title={t("Suggestions")}
                  tag={
                    <span className={`flex items-center text-xs font-semibold bg-purple/30 text-purple border rounded-full px-2 py-0.5 text-center`}>AI <SparklesIcon className="w-4 h-4 ml-1 -mt-1 flex-shrink-0" /></span>
                  }
                  isOpen={openSections.has("SUGGESTIONS")}
                  onToggle={() => toggleSection("SUGGESTIONS")}
                  hidden={totalSuggestions === 0}
                >
                  <div className="flex flex-col gap-3">
                    <div key={currentOutput?.id} className="border border-gray-200 rounded-xl overflow-hidden">

                      {currentOutput?.suggestions.length === 0 &&
                        <div className="mx-2 my-4 text-sm text-gray-400 font-semibold">
                          {t("No suggestions were generated for this output")}
                        </div>
                      }

                      <div className="flex flex-col">
                        {currentOutput?.suggestions.map(suggestion => {
                          const isPending = suggestionPendingId === suggestion.id;
                          const error = suggestionErrors[suggestion.id];
                          const isApplied = suggestion.status === "APPLIED";
                          const isDismissed = suggestion.status === "DISMISSED";
                          return (
                            <div
                              key={suggestion.id}
                              className={`flex flex-col gap-2 px-3 py-3 border-b border-gray-100 last:border-b-0 ${isDismissed ? "bg-gray-50 opacity-70" : ""}`}
                            >
                              <div className={`text-sm leading-relaxed ${isDismissed ? "text-gray-500" : "text-gray-800"}`}>
                                {suggestion.description}
                              </div>
                              {suggestion.rationale && (
                                <div className={`text-xs leading-relaxed italic ${isDismissed ? "text-gray-400" : "text-gray-500"}`}>
                                  {suggestion.rationale}
                                </div>
                              )}
                              {(isApplied || isDismissed) && (
                                <div className="flex items-center gap-2">
                                  {isApplied
                                    ? <div className="text-xs font-semibold text-green">{t("Applied")}</div>
                                    : <div className="text-xs font-semibold text-gray-500">{t("Dismissed")}</div>
                                  }
                                  {canApprove && (isDismissed || suggestion.hasReverseActions) && (
                                    <button
                                      type="button"
                                      className="text-xs font-semibold text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50"
                                      onClick={() => handleUndoSuggestion(suggestion)}
                                      disabled={isPending}
                                    >
                                      {isPending ? t("Undoing...") : t("Undo")}
                                    </button>
                                  )}
                                </div>
                              )}
                              {error && (
                                <div className="text-xs text-red bg-red/10 rounded p-2">{error}</div>
                              )}
                              {suggestion.status === "PENDING" && canApprove && (
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="text-sm font-semibold py-1.5 px-3 rounded-md bg-blue text-white hover:bg-blue/90 disabled:opacity-50"
                                    onClick={() => handleApplySuggestion(suggestion)}
                                    disabled={isPending}
                                  >
                                    {isPending ? t("Applying...") : (error ? t("Try again") : t("Apply"))}
                                  </button>
                                  <button
                                    type="button"
                                    className="text-sm font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                    onClick={() => handleDismissSuggestion(suggestion)}
                                    disabled={isPending}
                                  >
                                    {t("Dismiss")}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>

                {/* ===== SIGNATURE REQUEST SECTION ===== */}
                <CollapsibleSection
                  title={t("Signature Request")}
                  statusLabel={sigStatusLabel}
                  tag={sigTag}
                  isOpen={openSections.has("SIGNATURES")}
                  onToggle={() => toggleSection("SIGNATURES")}
                  hidden={!hasSignatureConfig}
                >
                  {!hasSignatureRequests ? (
                    <div className="text-sm text-gray-400">{t("Signature Requests will be generated after document approvals.")}</div>
                  ) : (<>
                    {(linkableSignatureRequets?.length ?? 0) > 0 && (
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-500 mb-1">{t("Preview Signable Documents")}</div>
                        {currentItem.signatureRequests?.map((signatureRequest, index) =>
                          <React.Fragment key={`link-${signatureRequest.id}`}>
                            {!isSendable(signatureRequest) &&
                              <div className="flex items-center gap-1 w-full">
                                <img className="h-4 w-4 flex-shrink-0" src={BLOCK_ICON_MAP["SIGNATURE_LINK"]} alt="File Icon" />
                                <a className="flex items-center gap-0.5 text-sm text-gray-600 hover:underline" href={signatureRequest.publicTokenUrl} target="_blank" rel="noreferrer">
                                  <div className="truncate w-[180px]">{signatureRequest.publicTokenUrl}</div>
                                  <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" />
                                </a>
                              </div>
                            }
                          </React.Fragment>
                        )}
                      </div>
                    )}

                    {(sendableSignatureRequets?.length ?? 0) > 0 && (() => {
                      const sorted = [...(sendableSignatureRequets ?? [])].sort((a, b) => a.recipientNumber - b.recipientNumber);
                      const firstExpiry = sorted[0]?.expiry;
                      const expiryDays = firstExpiry ? moment(firstExpiry).diff(moment(), "days") : null;
                      const isActiveRecipient = (sr: SignatureRequest) => ["REQUESTED", "ACCESSED"].includes(sr.status);

                      const incompleteSignatureRequests = sorted.filter(sr => sr.status !== "COMPLETED");
                      const sequenceNotBegun = incompleteSignatureRequests.every(sr => sr.status === "PENDING");

                      const sigDisconnected = !emailConnected && sequenceNotBegun;

                      return (
                        <div className={`border rounded-xl ${(currentOutputGroup === "SIGNATURE_REQUESTS" || sigDisconnected) ? "border-blue" : "border-gray-200"}`}>
                          <div className="flex flex-col p-3 gap-2">
                            {/* Card header: email icon + label + expiry/disconnect */}
                            <div className="flex items-center gap-2">
                              <img className="h-3 w-3 flex-shrink-0" src={BLOCK_ICON_MAP[workflow.signatureConfig?.emailType ?? "GMAIL"]} alt="Email Icon" />
                              <span className="text-sm font-semibold text-black">{t("Signature Request")}</span>
                              {sigDisconnected ? (
                                <div className="flex items-center gap-1 ml-auto">
                                  <div className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                                  <span className="text-xs font-semibold text-orange">{t("Not connected")}</span>
                                </div>
                              ) : (
                                expiryDays !== null && expiryDays >= 0 && (
                                  <span className="ml-auto text-xs font-semibold text-gray-500">{t("Expires in {{days}}d", { days: expiryDays })}</span>
                                )
                              )}
                            </div>

                            {/* Recipient rows */}
                            {sorted.map((sr, _i) => {
                              const num = sr.recipientNumber;
                              const color = RECIPIENT_COLORS[(num - 1) % RECIPIENT_COLORS.length];
                              const name = recipientNameMap[num] || sr.recipientEmail;
                              const isActive = isActiveRecipient(sr);

                              // Status label + color
                              let statusText = "";
                              let statusColor = "text-gray-400";
                              if (sr.status === "COMPLETED") { statusText = t("Signed"); statusColor = "text-green"; }
                              else if (sr.status === "REQUESTED") { statusText = t("Sent"); statusColor = "text-blue"; }
                              else if (sr.status === "ACCESSED") { statusText = t("Viewed"); statusColor = "text-blue"; }
                              else if (sr.status === "PENDING") { if (!sequenceNotBegun) { statusText = t("Waiting..."); statusColor = "text-gray-400"; } }
                              else if (sr.status === "CANCELED") { statusText = t("Canceled"); statusColor = "text-yellow"; }
                              else if (sr.status === "EXPIRED") { statusText = t("Expired"); statusColor = "text-yellow"; }
                              else if (sr.status === "ERROR") { statusText = t("Error"); statusColor = "text-red"; }

                              return (
                                <div
                                  key={sr.id}
                                  className={`flex flex-col justify-center min-h-[40px] px-2 py-2.5 rounded-md cursor-pointer border ${sr.id === currentSignatureRequest?.id ? "selected-item-border" : "border-gray-200 hover:bg-gray-50"} ${sigDisconnected ? "opacity-30 pointer-events-none" : ""}`}
                                  onClick={() => {
                                    markOutputViewed(sr.id);
                                    const idx = currentItem.signatureRequests?.findIndex(s => s.id === sr.id) ?? -1;
                                    if (idx >= 0) setCurrentItemOutput("SIGNATURE_REQUESTS", idx);
                                  }}
                                >
                                  {/* Recipient info row */}
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs leading-4 font-semibold text-gray-400 w-4 h-4 rounded-sm bg-gray-100 text-center flex-shrink-0">{num}</span>
                                    <div className="flex items-center gap-1 min-w-0">
                                      <UserCircleIcon className={`w-5 h-5 flex-shrink-0 ${color}`} />
                                      <span className={`text-sm truncate font-semibold ${sr.id === currentSignatureRequest?.id ? "text-black" : "text-gray-600"}`}>{name}</span>
                                    </div>
                                    {recipientNameMap[num] && <span className="text-sm text-gray-400 truncate hidden sm:inline">{sr.recipientEmail}</span>}
                                    {statusText && <span className={`ml-auto text-xs font-semibold whitespace-nowrap ${statusColor}`}>{statusText}</span>}
                                    {sequenceNotBegun && _i > 0 && !viewedOutputIds.has(sr.id) && <div className="bg-green w-2 h-2 rounded-full flex-shrink-0 ml-auto" />}
                                  </div>

                                  {/* Action buttons - only shown when active and connected */}
                                  {isActive && !sigDisconnected && (
                                    <>
                                      <div className="border-t border-gray-200 my-2" />
                                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                        {resendSignatureRequestId !== sr.id && (
                                          <button className="flex-1 text-sm font-semibold py-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50" onClick={() => handleResendSignatureRequest(sr.id)}>
                                            {t("Resend Email")}
                                          </button>
                                        )}
                                        {resendSignatureRequestId === sr.id && resendStatus === "SENDING" && (
                                          <button className="flex-1 text-sm font-semibold py-1.5 rounded border border-gray-200 text-gray-400" disabled>
                                            {t("Sending...")}
                                          </button>
                                        )}
                                        {resendSignatureRequestId === sr.id && resendStatus === "RESENT" && (
                                          <button className="flex-1 text-sm font-semibold py-1.5 rounded border border-green text-green cursor-not-allowed" disabled>
                                            {t("Resent")}
                                          </button>
                                        )}
                                        {resendSignatureRequestId === sr.id && resendStatus === "ERROR" && (
                                          <button className="flex-1 text-sm font-semibold py-1.5 rounded border border-red text-red cursor-not-allowed" disabled>
                                            {t("Error")}
                                          </button>
                                        )}
                                        <Popover className="relative" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                          <Popover.Button
                                            type="button"
                                            className={`flex items-center justify-center p-2 rounded border bg-white border-gray-200 hover:bg-gray-100`}
                                          >
                                            {copiedSignatureRequest === sr.id
                                              ? <CheckIcon className="w-4 h-4 text-green" />
                                              : <DotsVerticalIcon className="w-4 h-4 text-gray-400" />
                                            }

                                          </Popover.Button>
                                          <Popover.Panel className="absolute mt-1 right-0 z-20 flex flex-col w-42 bg-white border rounded shadow overflow-y-auto">
                                            <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={() => openModal(<ChangeRecipientModal onConfirm={handleChangeRecipients} />)}>
                                              <UserCircleIcon className="w-4 h-4 mr-2" /> {t("Change recipient")}
                                            </Popover.Button>
                                            <CopyToClipboard text={sr.publicTokenUrl} onCopy={() => handleOnCopiedSignatureRequest(sr.id)}>
                                              <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap">
                                                <ClipboardCopyIcon className="w-4 h-4 mr-2" /> {t("Copy signable link")}
                                              </Popover.Button>
                                            </CopyToClipboard>
                                            <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={() => window.open(sr.publicTokenUrl, "_blank")}>
                                              <ExternalLinkIcon className="w-4 h-4 mr-2" /> {t("Open signable document")}
                                            </Popover.Button>
                                          </Popover.Panel>
                                        </Popover>
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Send sequence / Send request button (only when first recipient is still PENDING and connected) */}
                          {currentItem.stage === "SIGNATURES_PENDING" && sequenceNotBegun && !sigDisconnected && (
                            <div className="flex flex-col gap-1 px-3 py-2.5 border-t border-gray-200">
                              {isSendingSequence ? (
                                <button className="w-full text-sm font-semibold py-2 rounded-md bg-blue/50 text-white" disabled>
                                  {t("Sending...")}
                                </button>
                              ) :
                                <button className="w-full text-sm font-semibold py-2 rounded-md bg-blue text-white hover:bg-blue/90" onClick={() => sendSignatureRequestSequence()}>
                                  {(sendableSignatureRequets?.length ?? 0) > 1 ? (incompleteSignatureRequests.length === sorted.length ? t("Send sequence") : t("Resume sequence")) : t("Send request")}
                                </button>
                              }
                              {(sendableSignatureRequets?.length ?? 0) > 1 && (
                                <div className="text-xs text-gray-400">
                                  {t("This will send the first email now and enable the following emails to be sent in sequence once the prior request is completed.")}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Reconnect bar (when disconnected and has pending sends) */}
                          {sigDisconnected && reconnectEmailBar}
                        </div>
                      );
                    })()}
                  </>)}
                </CollapsibleSection>

                {/* ===== EMAIL SECTIONS (one per workflow email block) ===== */}
                {workflow?.emails?.map((emailConfig, emailIdx) => {
                  const matchingOutputEmail = currentItem.outputEmails?.find(oe => oe.email === emailConfig.id);
                  const emailOutputIndex = currentItem.outputEmails?.findIndex(oe => oe.email === emailConfig.id) ?? -1;
                  const emailStatusLabel = (matchingOutputEmail?.status === "PENDING" && ["EMAIL", "FINALISED", "SIGNATURES_COMPLETED"].includes(currentItem.stage)) ? t("Action Required") : undefined;
                  const autoSendTag = emailConfig.enableDraft === false
                    ? <StatusPill text={t("Auto-Send: On")} color="green" />
                    : <StatusPill text={t("Auto-Send: Off")} color="gray" />;
                  const emailSentTag = matchingOutputEmail?.status === "SENT"
                    ? <StatusPill text={t("Sent")} color="green" />
                    : autoSendTag;
                  const emailDisconnected = !emailConnected && matchingOutputEmail?.status === "PENDING";

                  return (
                    <CollapsibleSection
                      key={`email-section-${emailIdx}`}
                      title={t("Email")}
                      statusLabel={emailStatusLabel}
                      tag={emailSentTag}
                      isOpen={openSections.has(`EMAIL_${emailIdx}`)}
                      onToggle={() => toggleSection(`EMAIL_${emailIdx}`)}
                    >
                      <div className={`border rounded-xl ${((currentOutputGroup === "EMAILS" && matchingOutputEmail?.id === currentOutputEmail?.id) || emailDisconnected) ? "border-blue" : "border-gray-200"}`}>
                        <div className="flex flex-col p-3 gap-2">
                          {/* Card header: email icon + subject + disconnect */}
                          <div className="flex items-center gap-2">
                            <img className="h-3 w-3 flex-shrink-0" src={BLOCK_ICON_MAP[emailConfig.emailType]} alt="Email Icon" />
                            <span className="text-sm font-semibold text-black truncate">
                              {matchingOutputEmail?.emailSubject ?? emailConfig.emailSubject ?? t("Email")}
                            </span>
                            {emailDisconnected && (
                              <div className="flex items-center gap-1 ml-auto">
                                <div className="w-1.5 h-1.5 rounded-full bg-orange" />
                                <span className="text-xs font-semibold text-orange">{t("Not connected")}</span>
                              </div>
                            )}
                            {matchingOutputEmail?.status === "SENT" && matchingOutputEmail?.emailType === "OUTLOOK" &&
                              <RefreshIcon className={`w-4 h-4 ml-auto text-gray-400 hover:text-gray-600 cursor-pointer ${checkingDeliveryEmailId === matchingOutputEmail.id ? "animate-spin" : "animate-none"}`} onClick={() => handleCheckDelivery(matchingOutputEmail.id, emailOutputIndex)} />
                            }
                          </div>

                          {/* Selector row: recipient count + attachment icon */}
                          <div className={emailDisconnected ? "opacity-30 pointer-events-none" : ""}>
                            {(() => {
                              const toAddresses = (matchingOutputEmail?.emailTo ?? emailConfig.emailTo ?? "").split(",").map(s => s.trim()).filter(Boolean);
                              const ccAddresses = (matchingOutputEmail?.emailCc ?? emailConfig.emailCc ?? "").split(",").map(s => s.trim()).filter(Boolean);
                              const allRecipients = [...toAddresses, ...ccAddresses];
                              const totalRecipients = allRecipients.length;
                              const hasAttachments = (matchingOutputEmail?.attachments?.length ?? emailConfig.attachments?.length ?? 0) > 0;
                              const recipientLabel = totalRecipients === 1 ? allRecipients[0] : t("{{count}} Recipients", { count: totalRecipients });

                              return (
                                <div
                                  className={`flex items-center gap-2 h-10 px-2 rounded-md cursor-pointer border border-100 ${currentOutputGroup === "EMAILS" && matchingOutputEmail?.id === currentOutputEmail?.id ? "selected-item-border" : "hover:bg-gray-50"}`}
                                  onClick={() => { if (emailOutputIndex >= 0) setCurrentItemOutput("EMAILS", emailOutputIndex); }}
                                >
                                  <UserCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <span className={`text-sm truncate font-semibold ${currentOutputGroup === "EMAILS" && matchingOutputEmail?.id === currentOutputEmail?.id ? "text-black" : "text-gray-600"}`}>{recipientLabel}</span>
                                  {hasAttachments && <img className="h-4 w-4 flex-shrink-0" src={BLOCK_ICON_MAP["PDF"]} alt="Attachment" />}
                                  {matchingOutputEmail?.status === "SENT" &&
                                    <>
                                      {matchingOutputEmail.deliveryStatus === "BOUNCED"
                                        ? <span className="ml-auto text-xs font-semibold text-red">{t("Bounced")}</span>
                                        : <span className="ml-auto text-xs font-semibold text-green">{t("Sent")}</span>
                                      }
                                    </>
                                  }
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Send button (only if PENDING, connected, and item is complete / ready to send) */}
                        {matchingOutputEmail?.status === "PENDING" && !emailDisconnected && currentItem.status === "COMPLETED" && hasPermission(workflow.team, "workflows.edit") && (
                          <div className="px-3 py-2.5 border-t border-gray-200">
                            {sidebarSendingEmailId === matchingOutputEmail?.id ? (
                              <button className="w-full text-sm font-semibold py-2 rounded-md bg-blue/50 text-white" disabled>
                                {t("Sending...")}
                              </button>
                            ) : matchingOutputEmail?.id === currentOutputEmail?.id ? (
                              <button
                                className="w-full text-sm font-semibold py-2 rounded-md bg-blue text-white hover:bg-blue/90"
                                onClick={() => {
                                  setSidebarSendingEmailId(matchingOutputEmail?.id);
                                  emailEditorRef.current?.triggerSend?.()?.finally(() => setSidebarSendingEmailId(undefined));
                                }}
                              >
                                {t("Send")}
                              </button>
                            ) : (
                              <button
                                className="w-full text-sm font-semibold py-2 rounded-md bg-blue/50 text-white"
                                onClick={() => { if (emailOutputIndex >= 0) setCurrentItemOutput("EMAILS", emailOutputIndex); }}
                              >
                                {t("Send")}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Reconnect bar (when disconnected and pending) */}
                        {emailDisconnected && reconnectEmailBar}
                      </div>
                    </CollapsibleSection>
                  );
                })}

                {/* ===== ACTIVITY SECTION ===== */}
                <CollapsibleSection
                  title={t("Activity")}
                  isOpen={openSections.has("ACTIVITY")}
                  onToggle={() => toggleSection("ACTIVITY")}
                >
                  <ActivityLog item={currentItem} workflow={workflow} />
                </CollapsibleSection>
              </div>
            }

            {!!showRequestChanges &&
              <div className="flex flex-col gap-1 h-full font-gilroy">
                <div className="flex items-center justify-between font-semibold text-black py-1">
                  {t("Request Changes")}
                </div>

                <div className="text-xs text-gray-400">
                  {t("Request any changes to be made. Add comments directly in the document for detailed feedback.")}
                </div>

                <div className="h-px w-full bg-gray-200 flex-shrink-0 my-4" />

                <div className="text-xs font-semibold text-gray-600 pb-2">
                  {t("Change request to be sent to:")}
                </div>
                {currentItem.initiatingUser &&
                  <div className="flex items-center px-2 gap-2">
                    <img className="h-8 w-8 rounded-full border-2 drop-shadow overflow-hidden" src={currentItem.initiatingUser.avatar} alt={"User Avatar"} />
                    <div className="flex flex-col items-start">
                      <div className="text-sm font-semibold text-gray-600">
                        {currentItem.initiatingUser.fullName}
                      </div>
                      <div className="text-xs text-gray-400">
                        {currentItem.initiatingUser.email}
                      </div>
                    </div>
                  </div>
                }

                <div className="text-xs font-semibold text-gray-600 mt-4">
                  {t("Message")}
                </div>
                <textarea className="w-full min-h-32 p-2 border border-gray-200 rounded resize-y font-eina text-sm" value={requestChangesMessage} placeholder={t("The document(s) requires updates before approval can continue. Please review the suggested edits and resolve the comments.")} onChange={(e) => setRequestChangesMessage(e.currentTarget.value)} />

                {(latestApprovalRequest?.approver.id === user.id) &&
                  <>
                    <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
                    <div className="flex flex-col items-center gap-2 mt-4">
                      {currentState === "INITIAL" &&
                        <button className="btn btn-blue text-sm h-10 w-full" onClick={handleRejectItem}>
                          {t("Submit")}
                        </button>
                      }
                      {currentState === "APPROVING" &&
                        <button className="btn btn-blue text-sm h-10 w-full" disabled>
                          {t("Processing...")}
                        </button>
                      }
                      {currentState === "DELETING" &&
                        <button className="btn btn-red text-sm h-10 w-full" disabled>
                          {t("Submitting...")}
                        </button>
                      }
                      {currentState === "APPROVED" &&
                        <button className="btn btn-green text-sm h-10 w-full" disabled>
                          {t("Submitted")}
                        </button>
                      }
                      {/* Request Changes button */}
                      {currentState === "INITIAL" &&
                        <button className="btn btn-gray text-sm h-10 w-full" onClick={() => setShowRequestChanges(false)}>
                          {t("Back")}
                        </button>
                      }
                    </div>
                  </>
                }
              </div>
            }
          </TransitionWorkflowPageSideBar>

          {currentOutputGroup === "DOCUMENTS" && currentOutput &&
            (() => {
              const isOneDrive = !!(currentOutput.onedriveFile || currentOutput.onedrivePdf);

              // Resolve display info based on provider and variant
              let displayName: string | undefined;
              let displayUrl: string | undefined;
              let displayMime: string;
              let previewContent: JSX.Element | null;

              if (currentDocVariant === "pdf") {
                // PDF variant: always use previewUrl with PDFViewer
                displayMime = "application/pdf";
                displayName = isOneDrive
                  ? (currentOutput.onedrivePdf?.fileName ?? currentOutput.onedriveFile?.fileName ?? currentOutput.outputName)
                  : (currentOutput.filePdf?.name ?? currentOutput.file?.name ?? currentOutput.hubspotFilePdf?.fileName ?? currentOutput.outputName);
                displayUrl = isOneDrive
                  ? (currentOutput.onedrivePdf?.fileUrl ?? currentOutput.onedriveFile?.fileUrl)
                  : (currentOutput.filePdf?.url ?? currentOutput.file?.url ?? currentOutput.hubspotFilePdf?.fileUrl);
                previewContent = currentOutput.previewUrl
                  ? <div className="w-full flex-1 min-h-0 border border-gray-300 rounded overflow-hidden"><PDFViewer pdfUrl={currentOutput.previewUrl} /></div>
                  : null;
              } else if (isOneDrive) {
                // Original variant: OneDrive embed
                const displayOD = currentOutput.onedriveFile ?? currentOutput.onedrivePdf;
                if (!displayOD) return null;
                displayName = displayOD.fileName;
                displayUrl = displayOD.fileUrl;
                displayMime = resolveMicrosoftMimeType(displayOD.fileMimeType, displayOD.fileName);
                previewContent = <iframe title={t("Microsoft File Viewer")} height="1000" className="w-full h-full border border-gray-300 rounded overflow-hidden" src={displayOD.fileEmbedViewUrl} />;
              } else {
                // Original variant: Google editor
                const displayFile = currentOutput.file ?? currentOutput.filePdf;
                displayMime = displayFile?.mimeType ?? (currentOutput.previewUrl ? "application/pdf" : "application/vnd.google-apps.document");
                displayName = displayFile?.name ?? currentOutput.outputName;
                displayUrl = displayFile?.url;

                if (displayFile && displayMime !== "application/pdf" && displayUrl)
                  previewContent = <iframe title={t("Google Editor")} className="w-full h-full border border-gray-300 rounded overflow-hidden" src={`${displayUrl}?widget&rm=embedded`} />;
                else if (currentOutput.previewUrl)
                  previewContent = <div className="w-full flex-1 min-h-0 border border-gray-300 rounded overflow-hidden"><PDFViewer pdfUrl={currentOutput.previewUrl} /></div>;
                else
                  previewContent = null;
              }

              return (
                <TransitionWorkflowPageContent key={`${viewingItemId ?? currentItem?.id}-doc-${currentOutput?.id ?? currentOutputIndex}-${currentDocVariant}`} className="relative">
                  <div className="flex w-full items-center mb-2">
                    <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
                      <img className="h-10 w-10 rounded-md" src={MIMETYPE_ICON_MAP[displayMime]} alt="File Icon" />
                    </div>
                    <div className="flex flex-col h-full justify-center">
                      <div className="flex text-sm font-semibold cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(displayUrl, "_blank")}>
                        <div className="truncate max-w-[400px]">{displayName}</div>
                        {displayUrl && <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />}
                      </div>
                    </div>
                    <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(basePath)} />
                  </div>
                  {previewContent ?? (
                    <div className="w-full flex-1 min-h-0 flex flex-col items-center justify-center gap-3 border border-gray-300 rounded bg-gray-50 text-center p-6">
                      <div className="text-sm font-semibold text-gray-600">{t("This preview isn't available yet.")}</div>
                      {displayUrl
                        ? <a href={displayUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-blue hover:underline">
                          {t("Open file")}<ExternalLinkIcon className="h-4 w-4" />
                        </a>
                        : <div className="text-xs text-gray-400 max-w-xs">{t("The document may still be generating. Try again in a moment.")}</div>
                      }
                    </div>
                  )}
                </TransitionWorkflowPageContent>
              );
            })()
          }

          {currentOutputGroup === "EMAILS" && currentOutputEmail &&
            <EmailEditor key={`${viewingItemId ?? currentItem?.id}-email-${currentOutputEmail?.id ?? currentOutputIndex}`} ref={emailEditorRef} initialEmail={currentOutputEmail} updateEmail={handleUpdateEmail} updateSender={updateSender} updateAttachment={updateEmailAttachment} sendEmail={currentItem?.status === "COMPLETED" ? handleSendEmail : undefined} onClose={() => history.push(basePath)} isSent={currentOutputEmail.status === "SENT"} disabled={currentOutputEmail.status === "SENT" || !hasPermission(workflow.team, "workflows.edit")} />
          }

          {currentOutputGroup === "SIGNATURE_REQUESTS" && currentSignatureRequest &&
            <React.Fragment key={`${viewingItemId ?? currentItem?.id}-sig-${currentSignatureRequest?.id ?? currentOutputIndex}`}>
              {["PREVIEW", "LINK", "LINK_UNPUBLISHED"].includes(currentSignatureRequest.status)
                ? <SignatureRequestLinkPreview signatureRequest={currentSignatureRequest} />
                : <EmailEditor title={t("Request Signatures")} ref={emailEditorRef} initialEmail={currentSignatureRequest} updateEmail={handleUpdateSignatureRequest} updateAttachment={updateSignatureRequestAttachment} onClose={() => history.push(basePath)} disabled={currentSignatureRequest.status !== "PENDING" || !hasPermission(workflow.team, "workflows.edit")} customTo={currentSignatureRequest.status !== "PENDING" ? <>{currentSignatureRequest.emailTo}</> : undefined} customBodyComponent={SignatureRequestEmailContant} overrideEmailType={workflow.signatureConfig?.emailType} overrideCustomFormatting={workflow.signatureConfig?.customFormatting} />
              }
            </React.Fragment>
          }

        </TransitionWorkflowPage>
      </div>
    </WorkflowOnlyContext.Provider>
  );

}

export default WorkflowOutputsDetail;
